export const PWA_INSTALLED_KEY = "reshet_pwa_installed";

export function markPwaInstalledLocally() {
  try {
    localStorage.setItem(PWA_INSTALLED_KEY, "1");
  } catch (_) {}
}

export function clearPwaInstalledLocally() {
  try {
    localStorage.removeItem(PWA_INSTALLED_KEY);
  } catch (_) {}
}

function isRunningAsInstalledApp() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.matchMedia("(display-mode: fullscreen)").matches) return true;
  if (window.matchMedia("(display-mode: minimal-ui)").matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
}

/** בדיקה סינכרונית — standalone / localStorage */
export function isPwaInstalled() {
  if (isRunningAsInstalledApp()) return true;
  try {
    return localStorage.getItem(PWA_INSTALLED_KEY) === "1";
  } catch {
    return false;
  }
}

/** בדיקה async — getInstalledRelatedApps (Chrome/Android) */
export async function detectPwaInstalled() {
  if (isPwaInstalled()) return true;

  if (typeof navigator !== "undefined" && "getInstalledRelatedApps" in navigator) {
    try {
      const apps = await navigator.getInstalledRelatedApps();
      if (apps?.length > 0) {
        markPwaInstalledLocally();
        return true;
      }
    } catch (_) {}
  }

  return false;
}

export function isIosDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/** Safari מקורי ב-iOS (לא Chrome/Firefox/Edge) */
export function isIosSafari() {
  if (!isIosDevice()) return false;
  const ua = navigator.userAgent || "";
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

export function isIosChrome() {
  if (!isIosDevice()) return false;
  return /CriOS/i.test(navigator.userAgent || "");
}

/** iOS / דפדפן מוטמע — אין beforeinstallprompt, רק הוספה ידנית למסך הבית */
export function needsManualPwaInstall() {
  if (isPwaInstalled()) return false;
  return isIosDevice() || isInAppBrowser();
}

export function isAndroidDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

export function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
    (navigator.maxTouchPoints > 1 && window.innerWidth < 1024)
  );
}

export function isInAppBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|Instagram|WhatsApp|Line\//i.test(ua);
}

export function isMacDesktop() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Macintosh|Mac OS X/i.test(ua) && !isMobileDevice() && !isAndroidDevice();
}

export function isLocalDevHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h.endsWith(".local");
}

export function canOfferPwaInstall() {
  return !isPwaInstalled();
}

function getForcedPwaPreviewPlatform() {
  if (typeof window === "undefined" || !isLocalDevHost()) return null;
  const value = new URLSearchParams(window.location.search).get("pwaPreview");
  if (value === "ios" || value === "android" || value === "mac") return value;
  return null;
}

function getIosInstallInstructions() {
  if (isInAppBrowser()) {
    return {
      title: "פתח ב-Safari להוספה למסך הבית",
      inAppBrowser: true,
      message: "WhatsApp / Facebook / Instagram לא מאפשרים הוספה למסך הבית:",
      steps: [
        "לחץ על ⋮ או «⋯» בפינה",
        "בחר «פתח ב-Safari» / «Open in Safari»",
        "ב-Safari: לחץ «הוסף למסך הבית» בתפריט → שיתוף (⬆️)",
      ],
    };
  }
  if (isIosChrome()) {
    return {
      title: "הוסף למסך הבית",
      variant: "ios-safari",
      steps: [],
    };
  }
  return {
    title: "הוסף למסך הבית",
    variant: "ios-safari",
    message: "",
    steps: [],
  };
}

function getPlatformPwaInstallInstructions() {
  const forced = getForcedPwaPreviewPlatform();
  if (forced === "ios" || (!forced && isLocalDevHost())) {
    return getIosInstallInstructions();
  }
  if (forced === "android") {
    return {
      title: "התקנת האפליקציה",
      steps: [
        "לחץ על ⋮ בפינה העליונה של Chrome",
        "בחר «הוסף למסך הבית» / «Install app»",
        "אשר — האייקון יופיע במסך הבית",
      ],
    };
  }
  if (forced === "mac" || isMacDesktop()) {
    return {
      title: "התקנה ב-Mac (Chrome / Edge)",
      steps: [
        "ודא שאתה ב-Chrome רגיל — לא Incognito",
        "גלוש ל-https://reshet.app",
        "בשורת הכתובת: לחץ על סמל ההתקנה (⊕ או «Install»)",
        "או: תפריט (⋮) → «Install Reset-APP…» / «התקן את…»",
      ],
    };
  }
  if (isInAppBrowser() && isIosDevice()) {
    return getIosInstallInstructions();
  }
  if (isInAppBrowser()) {
    return {
      title: "פתח בדפדפן להתקנה",
      inAppBrowser: true,
      message: "מ-WhatsApp / Facebook / Instagram צריך לפתוח בדפדפן:",
      steps: [
        "לחץ על ⋮ או «⋯»",
        "בחר «פתח בדפדפן» / «Open in Chrome»",
        "לאחר מכן לחץ שוב על «הוסף למסך הבית»",
      ],
    };
  }
  if (isIosDevice()) {
    return getIosInstallInstructions();
  }
  if (isAndroidDevice()) {
    return {
      title: "התקנת האפליקציה",
      steps: [
        "לחץ על ⋮ בפינה העליונה של Chrome",
        "בחר «הוסף למסך הבית» / «Install app»",
        "אשר — האייקון יופיע במסך הבית",
      ],
    };
  }
  return {
    title: "התקנת האפליקציה",
    steps: [
      "ודא שאתה ב-Chrome רגיל — לא Incognito",
      "ב-Chrome: לחץ על סמל ההתקנה בשורת הכתובת",
      "או: תפריט (⋮) → «התקן את…» / «Install app»",
    ],
  };
}

export function getPwaInstallInstructions() {
  const instructions = getPlatformPwaInstallInstructions();
  if (isLocalDevHost()) {
    return {
      ...instructions,
      devPreview: true,
      devNotice: undefined,
    };
  }
  return instructions;
}
